import { useState, type ReactNode } from "react";
import { useShowContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Show } from "@/components/admin/show";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { formatMxn } from "../quoter/PlanSchedule";
import { ExpenseCancelDialog } from "./ExpenseCancelDialog";
import { CancelledChip, InvoicedPill, usePaidByLabel } from "./ExpenseBadges";
import { ExpenseFileLink } from "./ExpenseFileField";
import { ExpenseReimbursementAction } from "./ExpenseReimburseDialog";
import { formatExpenseDate } from "./expenseScreenRules";
import type { Expense } from "./expenseTypes";

const EMPTY = "—";

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm tabular-nums">{children}</dd>
  </div>
);

/** Reimbursement status (only for expenses paid by Pepe Diaz); the action sits next to Editar. */
const ReimbursementChip = ({ expense }: { expense: Expense }) => {
  const translate = useTranslate();
  if (expense.paid_by !== "pepe_diaz" || expense.cancelled_at) return null;
  return expense.reimbursed ? (
    <StatusPill
      tone="ok"
      text={translate("crm.expenses.reimbursed", {
        date: formatExpenseDate(expense.reimbursed_at),
      })}
    />
  ) : (
    <StatusPill
      tone="not-ok"
      text={translate("crm.expenses.pending_reimbursement")}
    />
  );
};

const FileFact = ({ label, path }: { label: string; path: string | null }) => (
  <Fact label={label}>
    {path ? <ExpenseFileLink path={path} label={label} /> : EMPTY}
  </Fact>
);

/** "Editar" (only while active) and "Cancelar gasto". There is no delete action. */
const ExpenseShowActions = () => {
  const translate = useTranslate();
  const { record } = useShowContext<Expense>();
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  if (!record || record.cancelled_at) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <ExpenseReimbursementAction expense={record} />
      <Button asChild variant="outline" className="min-h-11">
        <Link to={`/expenses/${record.id}`}>{translate("ra.action.edit")}</Link>
      </Button>
      <Button
        type="button"
        variant="destructive"
        className="min-h-11"
        onClick={() => setIsCancelOpen(true)}
      >
        {translate("crm.expenses.cancel.action")}
      </Button>
      <ExpenseCancelDialog
        expense={record}
        open={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
      />
    </div>
  );
};

const ExpenseShowLayout = () => {
  const { record, isPending } = useShowContext<Expense>();
  const translate = useTranslate();
  const paidByLabel = usePaidByLabel();
  if (isPending || !record) return <ListCardsSkeleton rows={3} />;
  const isCancelled = record.cancelled_at != null;

  return (
    <Card>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-xl font-semibold">{record.concept}</h2>
          {isCancelled ? <CancelledChip /> : null}
          <InvoicedPill invoiced={record.is_invoiced} />
          <ReimbursementChip expense={record} />
        </div>
        {isCancelled ? (
          <p
            data-testid="expense-cancel-info"
            className="rounded-md bg-muted/50 p-3 text-sm"
          >
            {translate("crm.expenses.cancel.info", {
              date: formatExpenseDate(record.cancelled_at),
              reason: record.cancel_reason ?? EMPTY,
            })}
          </p>
        ) : null}
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Fact label={translate("crm.expenses.fields.date")}>
            {formatExpenseDate(record.expense_date)}
          </Fact>
          <Fact label={translate("crm.expenses.fields.category")}>
            {record.category_name}
          </Fact>
          <Fact label={translate("crm.expenses.fields.supplier")}>
            {record.supplier ?? EMPTY}
          </Fact>
          <Fact label={translate("crm.expenses.fields.amount")}>
            <span className="font-semibold">
              {formatMxn(record.amount_mxn)}
            </span>
          </Fact>
          <Fact label={translate("crm.expenses.fields.subtotal")}>
            {formatMxn(record.subtotal_mxn)}
          </Fact>
          <Fact label={translate("crm.expenses.fields.iva")}>
            {record.has_iva ? formatMxn(record.iva_amount) : EMPTY}
          </Fact>
          <Fact label={translate("crm.expenses.fields.paid_by")}>
            {paidByLabel(record)}
          </Fact>
          <Fact label={translate("crm.expenses.fields.payment_method")}>
            {translate(`crm.expenses.payment_methods.${record.payment_method}`)}
          </Fact>
          <Fact label={translate("crm.expenses.fields.project")}>
            {record.project_name ?? translate("crm.expenses.no_project")}
          </Fact>
          <Fact label={translate("crm.expenses.fields.cfdi_uuid")}>
            <span data-testid="expense-cfdi-uuid">
              {record.cfdi_uuid ?? EMPTY}
            </span>
          </Fact>
          <FileFact
            label={translate("crm.expenses.fields.receipt")}
            path={record.receipt_path}
          />
          <FileFact
            label={translate("crm.expenses.fields.cfdi_pdf")}
            path={record.cfdi_pdf_path}
          />
          <FileFact
            label={translate("crm.expenses.fields.cfdi_xml")}
            path={record.cfdi_xml_path}
          />
          <Fact label={translate("crm.expenses.fields.notes")}>
            {record.notes ?? EMPTY}
          </Fact>
        </dl>
      </CardContent>
    </Card>
  );
};

/**
 * Almikia Epic EG (TASK-044): detail of an expense with signed-URL file links,
 * the reimbursement chip and "Cancelar gasto" (mandatory reason). A cancelled
 * expense stays visible, read-only, with its reason. No delete anywhere.
 */
export const ExpenseShow = () => (
  <Show actions={<ExpenseShowActions />}>
    <ExpenseShowLayout />
  </Show>
);
